import './error-template.scss';

import { useTranslation } from 'react-i18next';

import { ROUTES } from '@/shared/routes';
import { Button, LocalizedLink } from '@/shared/ui';

export type TErrorType = 'notFound' | 'oops';

interface IProps {
  type: TErrorType;
  /** Повтор неудавшейся загрузки. С ним «Повторить» — главное действие, а переход на главную — вторичное. */
  onRetry?: () => void;
}

/** Компас — «свернули не туда» (страница не найдена). Стиль иконок дома: stroke + currentColor. */
const CompassIcon: React.FC = () => (
  <svg
    className="error-template__icon"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M15.6 8.4l-2 5.2-5.2 2 2-5.2 5.2-2z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

/** Треугольник-предупреждение — что-то пошло не так (рантайм-ошибка). */
const AlertIcon: React.FC = () => (
  <svg
    className="error-template__icon"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M10.3 4.2 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M12 9.5v4M12 17h.01"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Экран-заглушка ошибки: тематическая иконка + заголовок/описание + кнопка на главную.
 * Презентационный, без зависимости от меню/бордера — переиспользуется и как 404-роут,
 * и как fallback для ErrorBoundary.
 */
const ErrorTemplate: React.FC<IProps> = ({ type, onRetry }) => {
  const { t } = useTranslation();
  const isOops = type === 'oops';

  return (
    <div className="error-template">
      <div
        className={`error-template__badge${
          isOops ? ' error-template__badge--oops' : ''
        }`}
      >
        {isOops ? <AlertIcon /> : <CompassIcon />}
      </div>

      <div className="error-template__text">
        <p className="error-template__title">
          {t(isOops ? 'errors.oops.title' : 'errors.notFound.title')}
        </p>
        <p className="error-template__description">
          {isOops ? (
            t('errors.oops.description')
          ) : (
            <>
              {t('errors.notFound.description1')}
              <br />
              {t('errors.notFound.description2')}
            </>
          )}
        </p>
      </div>

      {onRetry ? (
        <div className="error-template__actions">
          <LocalizedLink
            to={ROUTES.home}
            className="error-template__home error-template__home--secondary"
          >
            {t('errors.redirectHome')}
          </LocalizedLink>
          <Button variant="filled-purple" onClick={onRetry}>
            {t('errors.retry')}
          </Button>
        </div>
      ) : (
        <LocalizedLink to={ROUTES.home} className="error-template__home">
          {t('errors.redirectHome')}
        </LocalizedLink>
      )}
    </div>
  );
};

export default ErrorTemplate;
